import Vue from 'vue'
import VueRouter from 'vue-router'
// https://github.com/declandewet/vue-meta
import VueMeta from 'vue-meta'
// Adds a loading bar at the top during page loads.
import NProgress from 'nprogress/nprogress'
import store from '@state/store'
import routes from './routes'
import _ from 'lodash'
import axios from 'axios'
import VueScrollTo from 'vue-scrollto'
import { getParameterCaseInsensitive, getSavedState } from '@state/helpers'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
})

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      VueScrollTo.scrollTo(to.hash, 700)
      return { selector: to.hash }
    } else if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  // If this isn't an initial page load...
  if (routeFrom.name !== null) {
    // Start the route progress bar.
    NProgress.start()
  }

  // Check if auth is required on this route
  // (including nested routes).
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired)
  // Check if session site ID exists
  let sessionReferralData = sessionGet('referralPartnerCode')
  // Check if session Partner ID exists
  let sessionReferralPartnerID = sessionGet('referralPartnerID')

  sessionReferralData = sessionReferralData
    ? sessionReferralData.toString()
    : sessionReferralData
  sessionReferralPartnerID = sessionReferralPartnerID
    ? sessionReferralPartnerID.toString()
    : sessionReferralPartnerID

  // Set siteID with order of priority - default to 0
  const paramSID = getParameterCaseInsensitive(routeTo.query, 'siteID')
  const siteID = paramSID || sessionReferralData || '0'
  const partnerID = sessionReferralPartnerID || '0'

  // Set default header to send attach to all requests
  axios.defaults.headers.common['X-Site-Id'] = `${siteID}`
  axios.defaults.headers.common['X-Partner-Id'] = `${partnerID}`

  // If the referrer ID is not already defined, get the referral data
  if (
    sessionReferralData === undefined ||
    siteID !== sessionReferralData ||
    !_.get(store, 'state.referrer.referrerData.menu') ||
    paramSID
  ) {
    store.dispatch('referrer/getData', siteID).then((response) => {
      axios.defaults.headers.common['X-Partner-Id'] = response
      // ACATB-5384 site id not always captured; pull from referrer data
      let partnerData = getSavedState('referrer.referrerData')
      let siteid = partnerData.siteID

      // Set session with referral partner code
      // ACATB-5834 might be 0 for invalid siteID
      sessionSet('referralPartnerCode', siteid, 'referralPartnerID', response)
      // Remove the product in cart if it doesn't exist with the new referral ID
      store.dispatch('customer/updateCart')
    })
  }

  // If auth isn't required for the route, just continue.
  if (!authRequired) {
    return next()
  }
  // // If auth is required and the user is logged in...
  if (store.getters['auth/loggedIn']) {
    // Validate the local user token...
    const currentUser = JSON.parse(localStorage.getItem('auth.currentUser'))

    if (
      currentUser.tokenExpiration <= Date.now() ||
      !currentUser.token ||
      !currentUser.tokenExpiration
    ) {
      // If token expired then logout user and clear localstorage
      store.dispatch('auth/logOut')
      // const authRequiredOnPreviousRoute = routeFrom.matched.some(
      //   (route) => route.meta.authRequired
      // )
      // Navigate back to previous page, or home as a fallback
      // next(authRequiredOnPreviousRoute ? { name: 'home' } : { ...routeFrom })

      localStorage.clear()
      // return next({ name: 'login' })
      return next({ name: 'login', query: { p: 2 } })
    } else {
      return next()
    }
  }

  // If auth is required and the user is NOT currently logged in,
  // redirect to login.
  redirectToLogin()

  function redirectToLogin() {
    // Pass the original route to the login component
    next({ name: 'login', query: { redirectFrom: routeTo.fullPath } })
  }

  // Get from session (if the value expired it is destroyed)
  function sessionGet(key) {
    let stringValue = window.sessionStorage.getItem(key)
    if (stringValue !== null) {
      let value = JSON.parse(stringValue)
      let expirationDate = new Date(value.expirationDate)
      if (expirationDate > new Date()) {
        return value.value
      } else {
        window.sessionStorage.removeItem(key)
      }
    }
    return null
  }

  // Add into session
  function sessionSet(
    keySiteID,
    valueSiteID = '0',
    keyPartnerID,
    valuePartnerID = '0',
    expirationInMin = 30
  ) {
    let expirationDate = new Date(
      new Date().getTime() + 60000 * expirationInMin
    )
    let newValueSiteID = {
      value: valueSiteID,
      expirationDate: expirationDate.toISOString(),
    }
    let newValuePartnerID = {
      value: valuePartnerID,
      expirationDate: expirationDate.toISOString(),
    }
    // store the site ID and partner ID as separate session storage items
    window.sessionStorage.setItem(keySiteID, JSON.stringify(newValueSiteID))
    window.sessionStorage.setItem(
      keyPartnerID,
      JSON.stringify(newValuePartnerID)
    )
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done()
              }
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
  // Complete the animation of the route progress bar.
  NProgress.done()
})

export default router
